/** @format */

import React, { useContext, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Highcharts from "highcharts";
import BasicChart from "highcharts-react-official";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import { GlobalContext } from "../../Contexts/GlobalContextProvider";
import { supportService } from "../../Services/support.service";
import SpinnerLoading from "../Common/SpinnerLoading";

const Statistics = () => {
  const { t: translator } = useTranslation();
  const { updateLocName } = useContext(GlobalContext);

  const [statistics, setStatistics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showChart, setShowChart] = useState(true);
  const [statsDuration, setStatsDuration] = useState("60");

  const [likes, setLikes] = useState([]);
  const [isLikesLoading, setIsLikesLoading] = useState(false);
  const [showLikesChart, setShowLikesChart] = useState(true);

  const [tableData, setTableData] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [selectedComments, setSelectedComments] = useState([]);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [latestComments, setLatestComments] = useState([]);
  const [isLatestLoading, setIsLatestLoading] = useState(false);

  useEffect(() => updateLocName("support-statistics"), []);

  useEffect(() => {
    setIsLikesLoading(true);
    supportService
      .likesStatistics()
      .then((res) => {
        setLikes(res.data.data);
        setIsLikesLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLikesLoading(false);
      });
  }, []);

  useEffect(() => {
    setIsTableLoading(true);
    supportService
      .commentsStatistics()
      .then((res) => {
        setTableData(res.data.data);
        setIsTableLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsTableLoading(false);
      });
  }, []);

  useEffect(() => {
    setIsLatestLoading(true);
    supportService
      .latestComments()
      .then((res) => {
        setLatestComments(res.data.data);
        setIsLatestLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLatestLoading(false);
      });
  }, []);

  useEffect(() => {
    setIsLoading(true);
    supportService
      .statistics(statsDuration ? parseInt(statsDuration) : undefined)
      .then((res) => {
        setStatistics(res.data.data);
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  }, [statsDuration]);

  const chartOptions = {
    chart: { type: "pie", height: "350px" },
    title: { text: null },
    tooltip: {
      pointFormat: "<b>{point.y}</b> pregleda ({point.percentage:.1f}%)",
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: { enabled: true, format: "<b>{point.name}</b>: {point.y}" },
      },
    },
    series: [
      {
        name: translator("Support-Statistics"),
        data: Array.isArray(statistics)
          ? statistics.map((item) => ({ name: item.name, y: item.count }))
          : [],
      },
    ],
    credits: { enabled: false },
  };

  const likesChartOptions = {
    chart: { type: "pie", height: "350px" },
    title: { text: null },
    tooltip: {
      pointFormat: "<b>{point.y}</b> lajkova ({point.percentage:.1f}%)",
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: { enabled: true, format: "<b>{point.name}</b>: {point.y}" },
      },
    },
    series: [
      {
        name: translator("Support-Positive"),
        data: Array.isArray(likes)
          ? likes.map((item) => ({ name: item.name, y: item.likesCount }))
          : [],
      },
    ],
    credits: { enabled: false },
  };

  return (
    <div className="p-2 pt-3">
      <div className="card container-fluid p-0">
        <div className="card-header card-wrap">
          <span>{translator("Support-Statistics")}</span>
        </div>
        <div className="card-body">
          <div className="row">

            {/* LEFT COLUMN - pregledi po trajanju */}
            <div className="col-md-6">
              <h6 className="font-weight-bold mb-3">{translator("Support-VisitsCount")}</h6>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <label className="mb-0">{translator("Support-MinDuration")}</label>
                  <select
                    className="form-control form-control-sm"
                    value={statsDuration}
                    onChange={(e) => setStatsDuration(e.target.value)}
                    style={{ width: "auto" }}
                  >
                    <option value="1">1s</option>
                    <option value="5">5s</option>
                    <option value="10">10s</option>
                    <option value="20">20s</option>
                    <option value="30">30s</option>
                    <option value="60">1m</option>
                    <option value="120">2m</option>
                    <option value="300">5m</option>
                    <option value="600">10m</option>
                  </select>
                </div>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => setShowChart((prev) => !prev)}
                >
                  {showChart ? translator("Support-ShowList") : translator("Support-ShowChart")}
                </button>
              </div>

              {isLoading ? (
                <div className="dashboard-card-spinner">
                  <SpinnerLoading />
                </div>
              ) : !showChart ? (
                <ul className="list-group">
                  <li
                    className="list-group-item d-flex justify-content-between align-items-center"
                    style={{ background: "#f8f9fa", fontWeight: 600, fontSize: 12, color: "#000" }}
                  >
                    <span>{translator("Support-Name")}</span>
                    <div className="d-flex" style={{ gap: 6 }}>
                      <span style={{ minWidth: 60, textAlign: "center" }}>Pregledi</span>
                      <span style={{ minWidth: 60, textAlign: "center" }}>Vrijeme (s)</span>
                    </div>
                  </li>
                  {Array.isArray(statistics) && statistics.map((item, i) => (
                    <li key={i} className="list-group-item d-flex justify-content-between align-items-center">
                      <span>{item.name}</span>
                      <div className="d-flex" style={{ gap: 6 }}>
                        <span className="badge badge-pill" style={{ minWidth: 60, backgroundColor: "#e20074", color: "#fff", fontSize: 14, padding: "6px 10px" }}>
                          {item.count}
                        </span>
                        <span className="badge badge-secondary badge-pill" style={{ minWidth: 60 }}>
                          {item.totalTimeSpentSeconds}s
                        </span>
                      </div>
                    </li>
                  ))}
                  {(!statistics || statistics.length === 0) && (
                    <li className="list-group-item text-muted">{translator("No-Data")}</li>
                  )}
                </ul>
              ) : (
                <BasicChart highcharts={Highcharts} options={chartOptions} />
              )}
            </div>

            {/* RIGHT COLUMN - lajkovi po temama */}
            <div className="col-md-6">
              <h6 className="font-weight-bold mb-3">{translator("Support-PositiveContent")}</h6>
              <div className="d-flex justify-content-end align-items-center mb-3">
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => setShowLikesChart((prev) => !prev)}
                >
                  {showLikesChart ? translator("Support-ShowList") : translator("Support-ShowChart")}
                </button>
              </div>

              {isLikesLoading ? (
                <div className="dashboard-card-spinner">
                  <SpinnerLoading />
                </div>
              ) : !showLikesChart ? (
                <ul className="list-group">
                  <li
                    className="list-group-item d-flex justify-content-between align-items-center"
                    style={{ background: "#f8f9fa", fontWeight: 600, fontSize: 12, color: "#000" }}
                  >
                    <span>{translator("Support-Name")}</span>
                    <span style={{ minWidth: 60, textAlign: "center" }}>Lajkovi</span>
                  </li>
                  {Array.isArray(likes) && likes.map((item, i) => (
                    <li key={i} className="list-group-item d-flex justify-content-between align-items-center">
                      <span>{item.name}</span>
                      <span className="badge badge-pill" style={{ minWidth: 60, backgroundColor: "#e20074", color: "#fff", fontSize: 14, padding: "6px 10px" }}>
                        {item.likesCount}
                      </span>
                    </li>
                  ))}
                  {(!likes || likes.length === 0) && (
                    <li className="list-group-item text-muted">{translator("No-Data")}</li>
                  )}
                </ul>
              ) : (
                <BasicChart highcharts={Highcharts} options={likesChartOptions} />
              )}
            </div>

          </div>

          {/* SECOND ROW */}
          <div className="row mt-4">

            {/* LEFT - statistika ocjena */}
            <div className="col-md-7">
              <hr />
              <h6 className="font-weight-bold mb-3">{translator("Support-RatingsStatistics")}</h6>
              {isTableLoading ? (
                <div className="dashboard-card-spinner">
                  <SpinnerLoading />
                </div>
              ) : (
                <BootstrapTable
                  keyField="supportId"
                  data={tableData}
                  columns={[
                    {
                      dataField: "name",
                      text: translator("Support-Title"),
                    },
                    {
                      dataField: "likesCount",
                      text: translator("Support-Positive"),
                      headerStyle: () => ({ width: "100px", textAlign: "center" }),
                      style: () => ({ textAlign: "center" }),
                      formatter: (cell) => (
                        <span className="badge badge-pill" style={{ backgroundColor: "#e20074", color: "#fff", minWidth: 40, fontSize: 14, padding: "6px 10px" }}>
                          {cell}
                        </span>
                      ),
                    },
                    {
                      dataField: "dislikesCount",
                      text: translator("Support-Negative"),
                      headerStyle: () => ({ width: "110px", textAlign: "center" }),
                      style: () => ({ textAlign: "center" }),
                      formatter: (cell) => (
                        <span className="badge badge-secondary badge-pill" style={{ minWidth: 40, fontSize: 14, padding: "6px 10px" }}>
                          {cell}
                        </span>
                      ),
                    },
                    {
                      dataField: "comments",
                      text: translator("Support-Comments"),
                      headerStyle: () => ({ width: "50px", textAlign: "center" }),
                      style: () => ({ textAlign: "center" }),
                      formatter: (cell) =>
                        Array.isArray(cell) && cell.length > 0 ? (
                          <span
                            style={{ cursor: "pointer", position: "relative", display: "inline-block" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedComments(cell);
                              setShowCommentsModal(true);
                            }}
                          >
                            <span className="icon icon-information" style={{ fontSize: 20 }}></span>
                           
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        ),
                    },
                  ]}
                  rowClasses="boostrap-table-row"
                  noDataIndication={translator("No-Data")}
                  pagination={paginationFactory({})}
                />
              )}
            </div>

            {/* RIGHT - zadnji komentari */}
            <div className="col-md-5">
              <hr />
              <h6 className="font-weight-bold mb-3">{translator("Support-LatestComments")}</h6>
              {isLatestLoading ? (
                <div className="dashboard-card-spinner"><SpinnerLoading /></div>
              ) : (
                <ul className="list-group">
                  {Array.isArray(latestComments) && latestComments.map((entry, i) => (
                    <li key={i} className="list-group-item">
                      <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>
                        {entry.name}
                        {entry.created && (
                          <span style={{ float: "right" }}>
                            {new Intl.DateTimeFormat("hr-HR", { dateStyle: "short", timeStyle: "short" }).format(new Date(entry.created))}
                          </span>
                        )}
                      </div>
                      <div>{entry.comment}</div>
                    </li>
                  ))}
                  {(!latestComments || latestComments.length === 0) && (
                    <li className="list-group-item text-muted">{translator("No-Data")}</li>
                  )}
                </ul>
              )}
            </div>

          </div>

        </div>
      </div>
      <Modal show={showCommentsModal} onHide={() => setShowCommentsModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{translator("Support-Comments-Modal")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedComments.length > 0 ? (
            <ul className="list-group list-group-flush">
              {selectedComments.map((c, i) => (
                <li key={i} className="list-group-item">{c}</li>
              ))}
            </ul>
          ) : (
            <span className="text-muted">{translator("No-Data")}</span>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Statistics;
